/**
 * FAVORITES — the worlds a user starred (☆/★, the SYQEL-wide favorites symbol — never a heart).
 *
 * Phase A is LOCAL-ONLY (no sign-in, no backend), but stored SYNC-READY: one record per world,
 * `{on, t}` — `t` the client clock (ms) of the last change, and `on:false` a TOMBSTONE rather than a
 * deletion. Phase B maps each record 1:1 onto the platform's last-writer-wins `/v1/state` key
 * `art.fav.<worldId>`, so an un-star made offline still wins over an older star from another device;
 * deleting the record instead would let that older star resurrect it on the next merge.
 *
 * Two surfaces read favorites:
 *  • the PLAYER (web/desktop) — its own store (`favorites`, below), persisted in localStorage;
 *  • a cast RECEIVER (the TV) — the CASTING SENDER's list, announced over the cast control channel as
 *    `{"favorites":["<worldId>", …]}` (newest first). It is SESSION state, like `entitled`: it belongs to
 *    that sender and goes when the cast does (setSenderFavorites / senderFavorites, below).
 */
import { Engine } from '@syqel/ota-client'

/** One world's favorite state. `on:false` is a tombstone (see the header) — never delete a record. */
export interface FavRecord { on: boolean; t: number }

const STORE_KEY = 'syqel.favs' // localStorage: {"v":1,"items":{"<worldId>":{"on":true,"t":1730000000000}}}
const STORE_VERSION = 1
/** Upper bound on a world id we accept from storage or the wire (fx ids are `<pack>/<slug>`). */
const MAX_ID_LEN = 128
/** Upper bound on records kept / ids accepted from one cast frame. The platform holds ≤1024 keys per
 *  user across every product, and no library is near this; anything larger is a broken writer. */
export const MAX_FAVORITES = 512

const validId = (id: unknown): id is string => typeof id === 'string' && id.length > 0 && id.length <= MAX_ID_LEN
const validRecord = (r: unknown): r is FavRecord => {
  if (!r || typeof r !== 'object') return false
  const o = r as { on?: unknown; t?: unknown }
  return typeof o.on === 'boolean' && typeof o.t === 'number' && Number.isFinite(o.t) && o.t >= 0
}

export class FavoritesStore {
  private items = new Map<string, FavRecord>()
  private readonly listeners = new Set<() => void>()

  constructor(private readonly key: string = STORE_KEY) {
    this.items = this.load()
    // another tab/window of this player starred something: same storage, so adopt it
    try {
      window.addEventListener('storage', (e) => {
        if (e.key !== this.key) return
        this.items = this.load()
        this.notify()
      })
    } catch { /* no window (tests) */ }
  }

  /** Is this world starred right now? */
  has(id: string): boolean {
    return this.items.get(id)?.on === true
  }

  /** Star or un-star a world. A no-op when nothing changes (no write, no notification). */
  set(id: string, on: boolean): void {
    if (!validId(id) || this.has(id) === on) return
    // t strictly increases per record even if the wall clock steps back, so the local order of
    // changes survives (the server clock is the authority across devices — phase B)
    const prev = this.items.get(id)
    const t = Math.max(Date.now(), prev ? prev.t + 1 : 0)
    this.items.delete(id) // re-insert → Map order = change order (cheap tie-break below)
    this.items.set(id, { on, t })
    this.trim()
    this.save()
    this.notify()
  }

  toggle(id: string): boolean {
    const on = !this.has(id)
    this.set(id, on)
    return on
  }

  /** The starred world ids, NEWEST FIRST — the order every surface (and the cast frame) uses. */
  list(): string[] {
    const on = [...this.items.entries()].filter(([, r]) => r.on)
    // ties on t (same ms) fall back to insertion order, newest last → reverse it
    return on.map(([id, r], i) => ({ id, t: r.t, i })).sort((a, b) => b.t - a.t || b.i - a.i).map((x) => x.id)
  }

  /** Every record INCLUDING tombstones — what phase B pushes to `/v1/state` (art.fav.<id> = {on,t}). */
  records(): ReadonlyMap<string, FavRecord> {
    return this.items
  }

  /** Subscribe to changes (this tab's or another's). Returns an unsubscribe function. */
  subscribe(cb: () => void): () => void {
    this.listeners.add(cb)
    return () => { this.listeners.delete(cb) }
  }

  private notify(): void {
    for (const cb of [...this.listeners]) { try { cb() } catch { /* a listener must not break the rest */ } }
  }

  /** Bound the record count: tombstones go first (oldest first), then the oldest stars. */
  private trim(): void {
    if (this.items.size <= MAX_FAVORITES) return
    const byAge = [...this.items.entries()].sort((a, b) => Number(a[1].on) - Number(b[1].on) || a[1].t - b[1].t)
    for (const [id] of byAge.slice(0, this.items.size - MAX_FAVORITES)) this.items.delete(id)
  }

  private load(): Map<string, FavRecord> {
    const out = new Map<string, FavRecord>()
    try {
      const raw = localStorage.getItem(this.key)
      if (!raw) return out
      const doc = JSON.parse(raw) as { v?: unknown; items?: unknown }
      if (doc.v !== STORE_VERSION || !doc.items || typeof doc.items !== 'object') return out
      const rows = Object.entries(doc.items as Record<string, unknown>).filter((e): e is [string, FavRecord] => validId(e[0]) && validRecord(e[1]))
      rows.sort((a, b) => a[1].t - b[1].t) // Map order = change order
      for (const [id, r] of rows.slice(-MAX_FAVORITES)) out.set(id, { on: r.on, t: r.t })
    } catch { /* private mode / corrupt — start empty */ }
    return out
  }

  private save(): void {
    try {
      localStorage.setItem(this.key, JSON.stringify({ v: STORE_VERSION, items: Object.fromEntries(this.items) }))
    } catch { /* private mode / quota — this session still has them */ }
  }
}

/** This player's favorites (web + desktop). */
export const favorites = new FavoritesStore()

// ── the DESKTOP as a cast SENDER ──────────────────────────────────────────────────────────────────
// The native cast announces the player's favorites to every TV it feeds (the LAN broadcast AND the
// pushed TV) as the `favorites` field of its sender-state frame — the same key the phone sends on its
// own. Hand Rust the list now and on every change; it reconciles connected receivers itself.
function pushCastFavorites(): void {
  if (typeof window === 'undefined' || !('__TAURI_INTERNALS__' in window)) return
  const ids = favorites.list()
  void import('@tauri-apps/api/core')
    .then(({ invoke }) => invoke('audio_cast_set_favorites', { favorites: ids }))
    .catch(() => { /* command absent (older shell) — ignore */ })
}
pushCastFavorites()
favorites.subscribe(pushCastFavorites)

// ── the RECEIVER side: the casting sender's favorites (session-scoped) ─────────────────────────────
let senderFavs: string[] = []
const senderListeners = new Set<() => void>()

/**
 * Validate a `favorites` value off the cast control channel (untrusted). Returns the ids to show —
 * known worlds only, de-duplicated, sender order kept, capped — or null when the value is not a
 * favorites list at all (the frame is then ignored, not treated as "no favorites").
 */
export function parseCastFavorites(v: unknown): string[] | null {
  if (!Array.isArray(v) || v.length > MAX_FAVORITES * 2) return null
  const known = new Set(Engine.WORLDS.map((w) => w.id))
  const seen = new Set<string>()
  const out: string[] = []
  for (const id of v) {
    if (!validId(id) || seen.has(id) || !known.has(id)) continue // a world this TV doesn't have can't be shown
    seen.add(id)
    out.push(id)
    if (out.length >= MAX_FAVORITES) break
  }
  return out
}

/** The cast receiver: the sender announced its favorites (or the cast ended → []). */
export function setSenderFavorites(ids: string[]): void {
  if (ids.length === senderFavs.length && ids.every((id, i) => id === senderFavs[i])) return
  senderFavs = ids
  for (const cb of [...senderListeners]) { try { cb() } catch { /* a listener must not break the rest */ } }
}

/** The connected sender's favorites, newest first ([] = no sender, or one without favorites). */
export function senderFavorites(): readonly string[] {
  return senderFavs
}

export function onSenderFavoritesChange(cb: () => void): () => void {
  senderListeners.add(cb)
  return () => { senderListeners.delete(cb) }
}
